/**
 * Row view model for the "Resetting in 72h" card of the Roost exceptions
 * dashboard (WI 34532). Split out of attention-view.logic.ts to keep both
 * files under the 500-line cap. Pure; no Angular.
 */
import { AccountSignal, SeatSignal } from './attention.logic';
import { formatDuration } from './wallboard.logic';

/** A seat/account's 7d window resets inside this horizon (Tim, WI 34532):
 * "those are the ones I really want to see", loaded with work before their
 * unused weekly capacity is lost for the week. */
export const RESET_SOON_WINDOW_MS = 72 * 60 * 60_000;

export interface ResetSoonRowVm {
  /** Lowercased account UPN: one row per account. */
  key: string;
  account: string;
  /** Every seat riding the account as "seat · machine", comma-separated, or
   * "no live seat". */
  seatsText: string;
  usedPct: number;
  /** The prominent figure: capacity about to be lost, not spent. */
  unusedPct: number;
  resetInText: string;
  resetAtMs: number;
}

/**
 * Every account whose 7-day window resets within 72h, soonest first
 * (WI 34532 AC). Unlike `buildAccountRows` this is NOT an exception rule: a
 * calm, low-usage account belongs here precisely because it is calm, it is
 * unused capacity about to be lost at reset, not a risk. One row per ACCOUNT,
 * keyed on the lowercased UPN (Tim, 2026-10-05: "We only care about account,
 * an account can have the same login on multiple seats"); the seats riding it
 * share one secondary line. An account with no live seat still gets its row
 * so its capacity isn't hidden.
 */
export function buildResetSoonRows(
  accounts: AccountSignal[],
  seats: SeatSignal[],
  nowMs: number
): ResetSoonRowVm[] {
  const seatById = new Map(seats.map((s) => [s.instanceId, s]));
  const byAccount = new Map<
    string,
    {
      account: AccountSignal;
      resetAtMs: number;
      percent: number;
      seatIds: string[];
    }
  >();
  for (const a of accounts) {
    const w = a.windows.find((x) => x.window === '7d');
    if (!w) continue;
    const untilReset = w.resetAtMs - nowMs;
    if (untilReset <= 0 || untilReset > RESET_SOON_WINDOW_MS) continue;
    const key = a.accountUpn.toLowerCase();
    const entry = byAccount.get(key) ?? {
      account: a,
      resetAtMs: w.resetAtMs,
      percent: w.percent,
      seatIds: [],
    };
    for (const id of a.seatIds) {
      if (!entry.seatIds.includes(id)) entry.seatIds.push(id);
    }
    byAccount.set(key, entry);
  }
  const rows: ResetSoonRowVm[] = [];
  for (const [key, e] of byAccount) {
    const usedPct = Math.round(e.percent);
    rows.push({
      key,
      account: e.account.accountUpn.split('@')[0],
      seatsText: e.seatIds.length
        ? e.seatIds
            .map((id) => `${id} · ${seatById.get(id)?.host ?? 'unknown'}`)
            .join(', ')
        : 'no live seat',
      usedPct,
      unusedPct: Math.max(0, 100 - usedPct),
      resetInText: `in ${formatDuration(e.resetAtMs - nowMs)}`,
      resetAtMs: e.resetAtMs,
    });
  }
  return rows.sort((x, y) => x.resetAtMs - y.resetAtMs);
}
